import { useCallback, useEffect, useState } from 'react';
import type {
  PropertySensorGroupRow,
  PropertyWithSensorGroups,
  UpdatePropertyRequest,
} from '@metron/shared';
import { validatePropertyForm } from '../../validators/property.ts';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Button } from '../ui/button.tsx';
import { Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { SensorGroupAssignment } from './SensorGroupAssignment.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { SensorGroupSourceTag, SensorGroupTypeTag } from './SensorGroupTags.tsx';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('PropertyRow');

interface PropertyEdit {
  id: number;
  name: string;
  metronId: string;
  customerId: number;
  createdAt: string;
  updatedAt: string;
  isNew?: boolean;
  isDeleted?: boolean;
  isDirty?: boolean;
  errors?: Record<string, string>;
  sensorGroups: PropertySensorGroupRow[];
}

interface PropertyRowProps {
  property: PropertyEdit;
  canEditProperties: boolean;
  allSensorGroups: PropertySensorGroupRow[];
  onPropertySave: (
    id: number,
    data: UpdatePropertyRequest,
  ) => Promise<PropertyWithSensorGroups>;
  onMarkForDeletion: (id: number) => void;
  onCancelNewProperty: (id: number) => void;
  onAssignSensorGroup: (propertyId: number, sensorGroupId: number) => Promise<void>;
  onStandaloneChange: (
    propertyId: number,
    oldSensorGroupId: number,
    newSensorGroupId: number | null,
  ) => Promise<void>;
  onAddMaster: (
    propertyId: number,
    childSensorGroupId: number,
    newMasterId: number,
  ) => Promise<void>;
  onRemoveMaster: (
    propertyId: number,
    masterSensorGroupId: number,
  ) => Promise<void>;
  onChildChange: (
    propertyId: number,
    masterSensorGroupId: number,
    newChildId: number | null,
  ) => Promise<void>;
  getEligibleForMasterRole: (excludeId?: number) => PropertySensorGroupRow[];
  getEligibleForChildRole: (
    propertyId: number,
    excludeId?: number,
  ) => PropertySensorGroupRow[];
  getEligibleForStandaloneAssignment: () => PropertySensorGroupRow[];
}

interface LocalPropertyState {
  name: string;
  metronId: string;
  errors: Record<string, string>;
  touched: Record<string, boolean>; // Track which fields have been blurred
  isDirty: boolean;
  isSaving: boolean;
}

export function PropertyRow({
  property,
  canEditProperties,
  allSensorGroups,
  onPropertySave,
  onMarkForDeletion,
  onCancelNewProperty,
  onAssignSensorGroup,
  onStandaloneChange,
  onAddMaster,
  onRemoveMaster,
  onChildChange,
  getEligibleForMasterRole,
  getEligibleForChildRole,
  getEligibleForStandaloneAssignment,
}: PropertyRowProps) {
  // LOCAL STATE - only affects this row
  const [localState, setLocalState] = useState<LocalPropertyState>({
    name: property.name,
    metronId: property.metronId,
    errors: property.errors || {},
    touched: {}, // Start with no fields touched
    isDirty: property.isDirty || false,
    isSaving: false,
  });

  // Track blank sensor group assignment cards that haven't been assigned yet
  const [blankAssignments, setBlankAssignments] = useState<Array<{ id: string }>>([]);

  // Track when we're assigning a sensor group (for loading indicator)
  const [isAssigning, setIsAssigning] = useState(false);

  // Sync local state from props when property changes (e.g., after save)
  // Only sync if we're not dirty (user hasn't made local changes)
  useEffect(() => {
    if (!localState.isDirty && !localState.isSaving) {
      setLocalState((prev) => ({
        ...prev,
        name: property.name,
        metronId: property.metronId,
        errors: property.errors || {},
      }));
    }
  }, [
    property.name,
    property.metronId,
    property.errors,
    localState.isDirty,
    localState.isSaving,
  ]);

  // Handle input changes locally (no parent updates)
  const handleInputChange = useCallback(
    (field: 'name' | 'metronId', value: string) => {
      setLocalState((prev) => ({
        ...prev,
        [field]: value,
        isDirty: true,
      }));
    },
    [],
  );

  // Validate on blur - only validate the specific field that was blurred
  const handleBlur = useCallback((field: 'name' | 'metronId') => {
    // Mark this field as touched
    setLocalState((prev) => ({
      ...prev,
      touched: {
        ...prev.touched,
        [field]: true,
      },
    }));

    // Validate all fields to get complete error state
    const allErrors = validatePropertyForm({
      name: localState.name,
      metronId: localState.metronId,
    });

    // Only show errors for fields that have been touched
    const filteredErrors: Record<string, string> = {};
    Object.keys(allErrors).forEach((key) => {
      if (localState.touched[key] || key === field) {
        filteredErrors[key] = allErrors[key];
      }
    });

    setLocalState((prev) => ({
      ...prev,
      errors: filteredErrors,
    }));
  }, [localState.name, localState.metronId, localState.touched]);

  // Save property
  const handleSave = useCallback(async () => {
    // Validate before saving
    const errors = validatePropertyForm({
      name: localState.name,
      metronId: localState.metronId,
    });

    if (Object.keys(errors).length > 0) {
      setLocalState((prev) => ({
        ...prev,
        errors,
      }));
      toast.error('Please fix validation errors before saving');
      return;
    }

    setLocalState((prev) => ({ ...prev, isSaving: true }));

    try {
      await onPropertySave(property.id, {
        name: localState.name,
        metronId: localState.metronId,
      });

      setLocalState((prev) => ({
        ...prev,
        isDirty: false,
        isSaving: false,
        errors: {},
        touched: {}, // Reset touched state after successful save
      }));

      toast.success('Property saved successfully');
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to save property');
      toast.error(errorMessage);
      setLocalState((prev) => ({ ...prev, isSaving: false }));
    }
  }, [property.id, localState.name, localState.metronId, onPropertySave]);

  // Cancel changes
  const handleCancel = useCallback(() => {
    if (property.isNew) {
      // Remove new unsaved property from list
      onCancelNewProperty(property.id);
    } else {
      // Revert to original values for existing property
      setLocalState({
        name: property.name,
        metronId: property.metronId,
        errors: {},
        touched: {}, // Reset touched state on cancel
        isDirty: false,
        isSaving: false,
      });
    }
  }, [property.id, property.name, property.metronId, property.isNew, onCancelNewProperty]);

  // Add a new blank sensor group assignment card
  const handleAddSensorGroupAssignment = useCallback(() => {
    const newBlankAssignment = {
      id: `blank-${Date.now()}`,
    };
    setBlankAssignments((prev) => [...prev, newBlankAssignment]);
  }, []);

  // Handle when user selects a sensor group in a blank assignment card
  const handleBlankAssignmentSelect = useCallback(
    async (blankId: string, sensorGroupId: number) => {
      // Remove the blank assignment card BEFORE making the API call
      // This prevents race conditions where the parent re-renders before we can clean up
      setBlankAssignments((prev) => prev.filter((b) => b.id !== blankId));
      setIsAssigning(true);

      try {
        await onAssignSensorGroup(property.id, sensorGroupId);
      } catch (err) {
        // Error is already handled and displayed in parent component
        logger.error({ err }, 'Failed to assign sensor group');
        // On error, don't re-add the blank card - the error toast is sufficient feedback
      } finally {
        setIsAssigning(false);
      }
    },
    [property.id, onAssignSensorGroup],
  );

  // Remove a blank assignment card without assigning
  const handleRemoveBlankAssignment = useCallback((blankId: string) => {
    setBlankAssignments((prev) => prev.filter((b) => b.id !== blankId));
  }, []);

  return (
    <div
      className={`flex items-start gap-4 p-4 border rounded-lg ${
        localState.isDirty ? 'border-yellow-500' : ''
      }`}
    >
      <div className='flex-1 grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div className='space-y-2'>
          <Label
            htmlFor={`name-${property.id}`}
            className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'
          >
            Property Name <span className='text-destructive'>*</span>
          </Label>
          <Input
            id={`name-${property.id}`}
            className='text-lg md:text-lg'
            value={localState.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            placeholder='Enter property name'
            disabled={localState.isSaving || !canEditProperties}
          />
          {localState.errors.name && (
            <p className='text-sm text-destructive'>
              {localState.errors.name}
            </p>
          )}
        </div>

        <div className='space-y-2'>
          <Label
            htmlFor={`metronId-${property.id}`}
            className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'
          >
            Metron ID <span className='text-destructive'>*</span>
          </Label>
          <Input
            id={`metronId-${property.id}`}
            className='text-lg md:text-lg'
            value={localState.metronId}
            onChange={(e) => handleInputChange('metronId', e.target.value)}
            onBlur={() => handleBlur('metronId')}
            placeholder='10-digit ID'
            maxLength={10}
            disabled={localState.isSaving || !canEditProperties}
          />
          {localState.errors.metronId && (
            <p className='text-sm text-destructive'>
              {localState.errors.metronId}
            </p>
          )}
        </div>

        {/* Save/Cancel buttons for property name and metron ID */}
        {localState.isDirty && (
          <div className='col-span-2 flex gap-2'>
            <Button
              variant='default'
              size='sm'
              onClick={handleSave}
              disabled={localState.isSaving || !canEditProperties}
            >
              <Save className='h-4 w-4 mr-2' />
              {localState.isSaving ? 'Saving...' : 'Save Property'}
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={handleCancel}
              disabled={localState.isSaving || !canEditProperties}
            >
              <X className='h-4 w-4 mr-2' />
              Cancel
            </Button>
          </div>
        )}

        <div className='col-span-2 space-y-4'>
          <Label className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
            Sensor Group Assignments
          </Label>

          {/* Show "No sensor groups" message only when there are truly none */}
          {property.sensorGroups.length === 0 && blankAssignments.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              No sensor groups assigned
            </p>
          )}

          {/* Sensor group assignment cards section */}
          <div className='space-y-6'>
            {/* Existing sensor groups */}
            {property.sensorGroups.map((sgRow) => (
              <SensorGroupAssignment
                key={`${property.id}-${sgRow.sensorGroupId}`}
                sensorGroup={sgRow}
                propertyId={property.id}
                availableSensorGroups={allSensorGroups}
                getEligibleForMasterRole={getEligibleForMasterRole}
                getEligibleForChildRole={getEligibleForChildRole}
                getEligibleForStandaloneAssignment={getEligibleForStandaloneAssignment}
                onAddMaster={(childSensorGroupId, newMasterId) => {
                  onAddMaster(property.id, childSensorGroupId, newMasterId);
                }}
                onRemoveMaster={(masterSensorGroupId) => {
                  onRemoveMaster(property.id, masterSensorGroupId);
                }}
                onChildChange={(masterSensorGroupId, newChildId) => {
                  onChildChange(property.id, masterSensorGroupId, newChildId);
                }}
                onStandaloneChange={(
                  oldSensorGroupId,
                  newSensorGroupId,
                ) => {
                  onStandaloneChange(property.id, oldSensorGroupId, newSensorGroupId);
                }}
              />
            ))}

            {/* Blank sensor group assignment cards */}
            {blankAssignments.map((blank) => {
              const eligibleGroups = getEligibleForStandaloneAssignment();
              return (
                <div key={blank.id} className='rounded-lg border bg-muted/30 p-4'>
                  <div className='flex items-center gap-2'>
                    <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0 text-right'>
                      Standalone:
                    </span>
                    <div className='flex-1'>
                      <Select
                        value='none'
                        onValueChange={(value) => {
                          if (value !== 'none') {
                            handleBlankAssignmentSelect(blank.id, Number(value));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select sensor group to assign...' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='none'>Select sensor group...</SelectItem>
                          {eligibleGroups.map((sg) => (
                            <SelectItem
                              key={sg.sensorGroupId}
                              value={sg.sensorGroupId.toString()}
                            >
                              <div className='flex items-center gap-2'>
                                <span className='font-medium'>{sg.sensorGroupName}</span>
                                {sg.sensorGroupMetronId && (
                                  <span className='text-sm text-muted-foreground'>
                                    ({sg.sensorGroupMetronId})
                                  </span>
                                )}
                                <SensorGroupSourceTag source={sg.sensorGroupSource} />
                                <SensorGroupTypeTag type={sg.waterscopeGroupType} />
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      className='w-48 shrink-0 justify-start'
                      onClick={() => handleRemoveBlankAssignment(blank.id)}
                      title='Cancel (remove blank assignment)'
                    >
                      <X className='h-4 w-4 mr-1' />
                      Cancel
                    </Button>
                  </div>
                </div>
              );
            })}

            {/* Loading indicator while assigning sensor group */}
            {isAssigning && (
              <div className='rounded-lg border bg-muted/30 p-4'>
                <div className='flex items-center gap-2'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  <span className='text-sm text-muted-foreground'>
                    Assigning sensor group...
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className='flex gap-2 justify-between'>
            {/* Only show Add Sensor Group Assignment button for saved properties */}
            {!property.isNew && (
              <Button
                variant='outline'
                size='sm'
                onClick={handleAddSensorGroupAssignment}
                disabled={!canEditProperties}
              >
                <Plus className='h-4 w-4 mr-2' />
                Add Sensor Group Assignment
              </Button>
            )}
            <Button
              variant='outline'
              size='sm'
              onClick={() => onMarkForDeletion(property.id)}
              className='border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground'
              disabled={localState.isSaving || property.isNew}
            >
              <Trash2 className='h-4 w-4 mr-2' />
              Delete Property
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
